import React, { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, Button, Alert, InputGroup, Form } from "react-bootstrap";
import Layout from "app/universal/layout/Layout";
import {
  initialize,
  getProducts,
  getProductGroups,
  getProductPackages,
  getProductClassifications,
  getProductParts
} from "state/adminData/actions";
import { getRotationSlots } from "state/rotationSlots/actions";
import {
  addOrder,
  fetchGroupedOrder,
  setCurrentOrderIndex,
  setOrderBriqueDevis,
  setOrderBriqueMode,
  updateOrderDeliveryCost,
} from "state/orderEdition/actions";
import {
  resetTenderCatalog
} from "state/tender/actions";
import { getTenderCatalog } from "state/tender/actions";
import CustomerSelectComponent from "./CustomerSelectComponent";
import AddCustomerComponent from "./AddCustomerComponent";
import CompanySelectComponent from "./CompanySelectComponent";
import AddCompanyComponent from "./AddCompanyComponent";
import TenderSelectComponent from "./TenderSelectComponent";
import FormatSelectComponent from "./FormatSelectComponent";
import DeliveryInfoComponent from "./DeliveryInfoComponent";
import BillingInfoComponent from "./BillingInfoComponent";
import ProductGroupsComponent from "./offer/ProductGroupsComponent";
import ProductPackagesComponent from "./offer/ProductPackagesComponent";
import ProductsComponent from "./offer/ProductsComponent";
import DeliveryFeeComponent from "./DeliveryFeeComponent";
import ReductionComponent from "./ReductionComponent";
import HeaderOrderInfoComponent from "./HeaderOrderInfoComponent";
import CommentaryOrderInfoComponent from "./CommentaryOrderInfoComponent";
import DiversOrderInfoComponent from "./DiversOrderInfoComponent";
import ListOrdersComponent from "./ListOrdersComponent";
import Loader from "app/deliveryTool/components/delivery/FakeDelivery";
import { useLocation, useHistory } from "react-router-dom";
import PricingTable from "./offer/caterer/PricingTable";
import { faUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import { toFrenchType } from "helper/globalHelper";
import {Helmet} from "react-helmet";
import AddAddressComponent from "./AddAddressComponent";
import ServiceInfoComponent from "./ServiceInfoComponent";
import BriquesSection from "./brique/BriquesSection";
import MarginSummaryPanel from "./MarginSummaryPanel";
import GabaritSelector from "app/orderTool/components/Gabarit/GabaritSelector";
import SaveGabaritModal from "app/orderTool/components/Gabarit/SaveGabaritModal";
import SavEnCoursBanner from "app/orderTool/components/SavEnCoursBanner";
import BriefPostIt from "app/orderTool/components/BriefPostIt";
import ClientFicheModal from "app/orderTool/components/ClientFicheModal";

/** Template API : show_by_default (JSON) ou showByDefault (camelCase) */
const templateIsShowByDefault = (t) =>
  t.show_by_default === true || t.showByDefault === true;

/**
 * Templates affichés par défaut : une brique « liée au format » n'est injectée
 * que si un format de prestation est choisi sur la commande.
 */
const templateMatchesOrderFormat = (template, formatTagId) => {
  const linked =
    template.linked_to_prestation_format === true ||
    template.linkedToPrestationFormat === true;
  if (!linked) {
    return true;
  }
  return !!formatTagId;
};

const EMPTY_BRIQUE_TEMPLATES = [];

const MultiOrders = () => {
  const dispatch = useDispatch();
  const isAdminDataLoading = useSelector((state) => state.adminData.isLoading);
  const isAdminDataInialized = useSelector(
    (state) => state.adminData.isInitialized
  );
  const isOrderEditionLoading = useSelector(
    (state) => state.orderEdition.isLoading
  );
  const productClassificationsState = useSelector(
    (state) => state.adminData.productClassifications
  );
  const productsState = useSelector((state) => state.adminData.products);
  const products = productsState.data;

  const productGroupsState = useSelector((state) => state.adminData.productGroups);
  const productGroups = productGroupsState.data;

  const productPackagesState = useSelector((state) => state.adminData.productPackages);
  const productPackages = productPackagesState.data;

  const productPartsState = useSelector((state) => state.adminData.productParts);
  const rotationSlotsState = useSelector((state) => state.rotationSlots);

  const tender = useSelector((state) => state.orderEdition.tender);
  const tenderCatalogState = useSelector((state) => state.tender.tenderCatalog);
  const tenderCatalog = useSelector((state) => state.tender.tenderCatalog.data);
  const briquesState = useSelector((state) => state.adminData.briques);
  const briquesTemplates = briquesState?.data ?? EMPTY_BRIQUE_TEMPLATES;
  const location = useLocation();
  const history = useHistory();

  const searchParams = new URLSearchParams(location.search);
  const groupedOrderId = searchParams.get("order_id");
  let orderIndex = searchParams.get('order_index');
  // Provenance de l'ouverture du devis : permet de proposer un vrai retour à
  // l'écran d'où l'on vient, la flèche du navigateur ne suffisant pas quand on
  // arrive ici juste après une transformation (retour Camille 15/09).
  const RETOURS = {
    pipeline: { chemin: "/pipeline-b2b", libelle: "Retour au pipeline" },
    sav: { chemin: "/pipeline-sav", libelle: "Retour au SAV" },
  };
  const retour = RETOURS[searchParams.get("from")] || null;

  const [showGabaritModal, setShowGabaritModal] = useState(false);
  const [showClientFiche, setShowClientFiche] = useState(false);

  const [isLoading, setIsLoading] = useState(
    isAdminDataLoading || isOrderEditionLoading
  );

  // Gére le loading
  useEffect(() => {
    if (isAdminDataLoading === false && isAdminDataInialized === false) {
      dispatch(initialize());
    } else {
      if (!productsState.isInitialized && !productsState.isLoading) {
        dispatch(getProducts());
      } 
      if (!productGroupsState.isInitialized && !productGroupsState.isLoading) {
        dispatch(getProductGroups());
      } 
      if (!productPackagesState.isInitialized && !productPackagesState.isLoading) {
        dispatch(getProductPackages());
      } 
      if (!productClassificationsState.isInitialized && !productClassificationsState.isLoading) {
        dispatch(getProductClassifications());
      }
      if (!productPartsState.isInitialized && !productPartsState.isLoading) {
        dispatch(getProductParts());
      }
      if (!rotationSlotsState?.isInitialized && !rotationSlotsState?.isLoading) {
        dispatch(getRotationSlots());
      }
    }
    setIsLoading(
      isAdminDataLoading || isOrderEditionLoading || productsState.isLoading || productGroupsState.isLoading || productPackagesState.isLoading || productClassificationsState.isLoading
        ? true
        : false
    );
  }, [
    isAdminDataLoading,
    isOrderEditionLoading,
    isAdminDataInialized,
    productsState,
    productClassificationsState,
    productGroupsState,
    productPackagesState,
    productPartsState,
  ]);

  // La commande
  useEffect(() => {
    if (groupedOrderId) {
      dispatch(fetchGroupedOrder(groupedOrderId));
    }
  }, [dispatch, groupedOrderId]);

  // Les prix AO
  useEffect(() => {
    if (tender?.id && tenderCatalogState.isLoading === false) {
      dispatch(getTenderCatalog(tender.id));
    }
    else if (!tender && tenderCatalogState.isLoading === false) {
      dispatch(resetTenderCatalog());
    }
  }, [dispatch, tender]);

  const currentOrderIndex = useSelector(
    (state) => state.orderEdition.currentOrderIndex
  );

  const orders = useSelector((state) => state.orderEdition.orders);

  // Rôle admin : le récap de marge n'est visible que pour les admins.
  const connectedUserRoles = useSelector(
    (state) => state.common?.user?.data?.roles || []
  );
  const isAdmin = [
    "ROLE_ADMIN",
    "ROLE_SUPER_ADMIN",
    "ROLE_DEV",
    "ROLE_ADMIN_PRO",
  ].some((r) => connectedUserRoles.includes(r));

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const newOrderIndex = searchParams.get('order_index');
    // Si pas de prestation, ne rien faire
    if (orders.length === 0) {
      return;
    }
    // Si orderIndex n'est pas présent, ajoutez-le avec une valeur de 0
    if (!searchParams.has('order_index')) {
      searchParams.set('order_index', 0);
      const newUrl = `${location.pathname}?${searchParams.toString()}`;
      history.push(newUrl);
      if (currentOrderIndex !== 0) {
        dispatch(setCurrentOrderIndex(0));
      }
    }
    // Si le currentOrderIndex est null et que le paramètre order_index est présent, définissez-le
    else if (searchParams.has('order_index') && currentOrderIndex === null && orders.length > 0 && !isLoading) {
      if (orders[newOrderIndex]) {
        dispatch(setCurrentOrderIndex(parseInt(newOrderIndex, 10)));
      }
      else {
        searchParams.set('order_index', 0);
        const newUrl = `${location.pathname}?${searchParams.toString()}`;
        history.push(newUrl);
        dispatch(setCurrentOrderIndex(0));
      }
    }
    // Si le paramètre order_index est présent et que la valeur est différente de l'index actuel, définissez-le
    else if (searchParams.has('order_index') && parseInt(newOrderIndex) !== currentOrderIndex && orders.length > 0 && !isLoading) {
      if (orders[newOrderIndex]) {
        dispatch(setCurrentOrderIndex(parseInt(newOrderIndex, 10)));
      }
      else {
        searchParams.set('order_index', 0);
        const newUrl = `${location.pathname}?${searchParams.toString()}`;
        history.push(newUrl);
        dispatch(setCurrentOrderIndex(0));
      }
    }
  }, [dispatch, orderIndex, isLoading, currentOrderIndex, orders]);

  const order = useSelector(
    (state) => state.orderEdition.orders[currentOrderIndex]
  );
  const customer = useSelector((state) => state.orderEdition.customer);
  const company = useSelector((state) => state.orderEdition.company);

  const prestationType = order?.prestationType;
  const formatTags = useSelector((state) => state.adminData.formatTags || []);
  const [selectedFormatTag, setSelectedFormatTag] = useState(null);
  const [showAddPrestationForm, setShowAddPrestationForm] = useState(false);
  const [addPrestationFormatTag, setAddPrestationFormatTag] = useState(null);

  // ---- Briques B2B : liste des briques par prestation (Redux) pour inclusion dans la sauvegarde API ----
  const briquesDevis = order?.briqueDevis ?? [];
  /** IDs template dont l’utilisateur a retiré la brique du devis (ne pas ré-injecter les « défauts ») */
  const dismissedDefaultTemplateIdsRef = useRef(new Set());

  useEffect(() => {
    dismissedDefaultTemplateIdsRef.current = new Set();
  }, [groupedOrderId]);

  // ---- Mode d'édition du devis : classique ou briques (par order) ----
  // Persisté dans Redux (order.briqMode) et sauvegardé côté API (is_brique_mode).
  // Cela permet de restaurer le mode après un rechargement de page.

  // Mode actif pour la prestation courante (lu depuis Redux)
  const briqueMode = order?.briqMode ?? false;
  const setBriqueMode = (val) => {
    if (currentOrderIndex !== null) {
      dispatch(setOrderBriqueMode(currentOrderIndex, val));
    }
  };

  // ---- Brique "livraison" : calcul automatique des frais de livraison ----
  // Si une brique dont le nom contient "livraison" existe, son total remplace
  // le champ manuel "Frais de livraison" et est dispatché dans deliveryCost.
  const briquelivraison = useMemo(() => {
    if (!briqueMode) return null;
    return briquesDevis.find(
      (b) => (b.name || "").toLowerCase().includes("livraison")
    ) || null;
  }, [briqueMode, briquesDevis]);

  const briquelivraisonTotal = useMemo(() => {
    if (!briquelivraison) return 0;
    return (briquelivraison.items || []).reduce((sum, item) => {
      if (item.type === "forfait") {
        const pp = item.price_per_person || 0;
        const nb = item.nb_persons || 0;
        return sum + pp * nb;
      }
      const price = item.unit_price || 0;
      const qty   = item.quantity   || 1;
      return sum + price * qty;
    }, 0);
  }, [briquelivraison]);

  // Synchronise automatiquement le deliveryCost quand la brique livraison change.
  // Sans brique livraison, les frais repassent à zéro : en mode briques le champ
  // « Frais de livraison » n'est pas affiché, et une valeur restée d'avant (mode
  // classique, devis dupliqué, brique livraison supprimée) était comptée dans le
  // total du BO sans être visible ni facturée (F/B/26/09/07644, Trello SSPNmz59).
  useEffect(() => {
    if (!briqueMode || currentOrderIndex === null) return;
    if (briquelivraison) {
      dispatch(updateOrderDeliveryCost(currentOrderIndex, briquelivraisonTotal));
    } else if (order?.deliveryCost) {
      dispatch(updateOrderDeliveryCost(currentOrderIndex, 0));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [briquelivraisonTotal, briquelivraison, briqueMode, currentOrderIndex, order?.deliveryCost]);

  // Auto-activation si la prestation a déjà des briques en cours de session
  useEffect(() => {
    if (currentOrderIndex === null) return;
    if (briquesDevis.length > 0 && !briqueMode) {
      dispatch(setOrderBriqueMode(currentOrderIndex, true));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrderIndex, briquesDevis.length]);

  // Le switch est autorisé seulement si le contenu de la prestation est vide :
  // → passer en mode classique : aucune brique dans cette prestation
  // → passer en mode briques   : aucun article classique dans cette prestation
  const classicItemCount = order?.items?.length ?? 0;
  const canSwitchToClassic  = briquesDevis.length === 0;
  const canSwitchToBriques  = classicItemCount === 0;
  // Le toggle est utilisable seulement dans la direction autorisée
  const canToggleBriqueMode = briqueMode ? canSwitchToClassic : canSwitchToBriques;

  // Briques « afficher par défaut » : les ajouter au devis à la création / édition si absentes
  // On ne les injecte PAS si la prestation a déjà des articles classiques (order.items non vide)
  // pour éviter d'activer le mode briques sur une prestation déjà saisie en mode classique.
  useEffect(() => {
    if (currentOrderIndex === null || !order) {
      return;
    }
    if (!isAdminDataInialized || !briquesState?.isInitialized) {
      return;
    }
    // Si la prestation a des articles classiques, ne pas injecter les briques par défaut
    if ((order?.items?.length ?? 0) > 0) {
      return;
    }
    dispatch(
      setOrderBriqueDevis(currentOrderIndex, (prev) => {
        const candidates = briquesTemplates.filter(
          (t) =>
            templateIsShowByDefault(t) &&
            templateMatchesOrderFormat(t, order.formatTagId) &&
            t.id != null &&
            !dismissedDefaultTemplateIdsRef.current.has(Number(t.id))
        );
        const missing = candidates
          .filter(
            (t) => !prev.some((b) => b.id != null && Number(b.id) === Number(t.id))
          )
          .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
        if (missing.length === 0) {
          return prev;
        }
        const stamp = Date.now();
        const newBriques = missing.map((template, idx) => ({
          ...template,
          _devisId: `devis_brique_${stamp}_${idx}_${template.id}`,
          items: [],
        }));
        return [...prev, ...newBriques];
      })
    );
  }, [
    dispatch,
    currentOrderIndex,
    order?.id,
    order?.formatTagId,
    briquesTemplates,
    briquesState?.isInitialized,
    isAdminDataInialized,
  ]);

  const handleRemoveBriqueFromDevis = (_devisId, brique) => {
    if (brique && brique.id != null) {
      dismissedDefaultTemplateIdsRef.current.add(Number(brique.id));
    }
  };

  const handleAddOrder = (prestationType, formatTag = null) => {
    const tagToUse = formatTag || selectedFormatTag;
    dispatch(addOrder(prestationType, tagToUse));
    const queryParams = new URLSearchParams(location.search);
    queryParams.set('order_index', orders.length);
    const newUrl = `${location.pathname}?${queryParams.toString()}`;
    history.push(newUrl);
  };

  const reloadOffer = () => {
    dispatch(initialize());
    dispatch(resetTenderCatalog());
    dispatch(getProducts());
    dispatch(getProductGroups());
    dispatch(getProductPackages());
    dispatch(getProductClassifications());
    dispatch(getProductParts());
    dispatch(getRotationSlots());
  };

  return (
    <Layout reduxIsUsed={true}>
      {order?.info?.groupedOrderNumber && order?.info?.orderNumber ? (
        <Helmet>
          <title>Devis {order?.info?.groupedOrderNumber} - {order?.info?.orderNumber}</title>
        </Helmet>
      ) : (
        <Helmet>
          <title>Nouveau devis</title>
        </Helmet>
      )}

      {isLoading ? (
        <>
          <Loader />
        </>
      ) 
      : order && (order.version !== "v2" || (order.status > 200 && order.status < 300)) ? (
        <div style={{ padding: "15px", textAlign: "center" }}>
          <SavEnCoursBanner clientId={customer?.id} entrepriseId={company?.id} />
          {/* Une commande ouverte depuis le SAV est souvent déjà facturée : le
              retour doit exister ici aussi, pas seulement dans l'éditeur. */}
          {retour && (
            <div style={{ textAlign: "left" }}>
              <Button variant="outline-secondary" size="sm" onClick={() => history.push(retour.chemin)}>
                ← {retour.libelle}
              </Button>
            </div>
          )}
          <h1>Commande non modifiable</h1>
          <p>
            {order.status > 200 && order.status < 400
              ? "Cette commande a été passé en facturation et n'est plus modifiable."
              : order.status >= 400 ?
                "Cette commande a été annulée et ne peut plus être modifiée."
              : "Cette commande a été créée avec une version antérieure de l'outil de commande et ne peut pas être modifiée."}
          </p>
          <Button
            variant="outline-success"
            size="sm"
            onClick={() => setShowGabaritModal(true)}
            style={{ marginTop: "10px" }}
          >
            Enregistrer en gabarit
          </Button>
          <SaveGabaritModal
            show={showGabaritModal}
            onHide={() => setShowGabaritModal(false)}
          />
        </div>
      ) : (
        <div
          id="multiorder"
        >
          <div className="sav-en-cours-strip">
            <SavEnCoursBanner clientId={customer?.id} entrepriseId={company?.id} />
          </div>
          <BriefPostIt groupedOrderId={groupedOrderId} />
          <div className="multiorder-body">
          <div style={{ display: "flex", justifyContent: retour ? "space-between" : "flex-end", padding: "8px 10px 0 10px" }}>
            {retour && (
              <Button variant="outline-secondary" size="sm" onClick={() => history.push(retour.chemin)}>
                ← {retour.libelle}
              </Button>
            )}
            <GabaritSelector />
          </div>
          <div
            id="customer"
          >
            <div>
              <label>Sélection du client*</label>
              <InputGroup>
                <CustomerSelectComponent />
                <InputGroup.Append>
                  <AddCustomerComponent />
                </InputGroup.Append>
              </InputGroup>
              {customer?.id && (
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 mt-1"
                  onClick={() => setShowClientFiche(true)}
                >
                  Voir la fiche client
                </Button>
              )}
              <br />
              <label>Sélection de l'entreprise</label>
              <InputGroup>
                <CompanySelectComponent />
                <InputGroup.Append>
                  <AddCompanyComponent />
                </InputGroup.Append>
              </InputGroup>
              <br />
              <TenderSelectComponent />
              <br />
              <hr />
              <AddAddressComponent title="Ajouter une adresse au carnet" />
              <hr />
              <br />
              <DeliveryInfoComponent title="Livraison :" />

              <BillingInfoComponent
                title="Facturation :"
                showCompany={true}
                dispatchAddress={false}
              />
            </div>
          </div>

          <ClientFicheModal
            show={showClientFiche}
            clientId={customer?.id}
            entrepriseId={company?.id}
            onClose={() => setShowClientFiche(false)}
          />

          {currentOrderIndex === null ? (
            <div
              id="main"
              style={{
                textAlign: "center",
              }}
            >
              <br />
              <br />
              <h2>Choisissez un format pour démarrer</h2>
              <p className="text-muted">
                Le type de prestation (PR/PDJ/TR) est déduit automatiquement du format choisi.
              </p>
              <br />
              {formatTags.length > 0 ? (
                <>
                  <Form.Group style={{ maxWidth: 400, margin: "0 auto 20px" }}>
                    <Form.Control
                      as="select"
                      value={selectedFormatTag ? selectedFormatTag.id : ""}
                      onChange={(e) => {
                        const id = e.target.value;
                        const tag = formatTags.find((t) => t.id === parseInt(id, 10));
                        setSelectedFormatTag(tag || null);
                      }}
                    >
                      <option value="">Choisir un format</option>
                      {formatTags.map((tag) => (
                        <option key={tag.id} value={tag.id}>
                          {tag.name}{tag.prestation_type ? ` (${toFrenchType(tag.prestation_type)})` : ""}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                  <div>
                    <Button
                      variant="primary"
                      onClick={() => handleAddOrder(selectedFormatTag?.prestation_type, selectedFormatTag)}
                      disabled={!selectedFormatTag || !selectedFormatTag.prestation_type}
                    >
                      + Ajouter
                      {selectedFormatTag?.prestation_type && (
                        <span className="ml-1 text-light" style={{ opacity: 0.85 }}>
                          ({toFrenchType(selectedFormatTag.prestation_type)})
                        </span>
                      )}
                    </Button>
                  </div>
                </>
              ) : (
                <p className="text-muted">Aucun format configuré. Contactez l'administrateur.</p>
              )}
            </div>
          ) : (
            <div id="main">
              <Row
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  border: "1px solid lightgray",
                  borderRadius: "10px",
                  margin: "10px 0",
                }}
              >
                <Col xs={12} sm={12} md={12} style={{ margin: "15px 0" }}>
                  <Alert variant="secondary">
                    {toFrenchType(order?.prestationType || "", true)} du{" "}
                    {order && order.deliveryInfo && order.deliveryInfo.deliveryDate
                      ? moment(order.deliveryInfo.deliveryDate).format(
                          "DD/MM/YY"
                        )
                      : "A RENSEIGNER"}{" "}
                    pour{" "}
                    {order && order.deliveryInfo && order.deliveryInfo.person
                      ? order.deliveryInfo.person
                      : "A RENSEIGNER"}{" "}
                    <FontAwesomeIcon icon={faUser} />
                    <br />
                  </Alert>
                  <FormatSelectComponent orderIndex={currentOrderIndex} />
                  {currentOrderIndex !== null ? (
                    <HeaderOrderInfoComponent orderIndex={currentOrderIndex} showStatus={false} tenderCatalog={tenderCatalog} products={products} productPackages={productPackages} productGroups={productGroups} />
                  ) : (
                    <Button variant="success" onClick={handleAddOrder}>
                      Ajouter une commande
                    </Button>
                  )}
                </Col>
              </Row>
              <Row
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  border: "1px solid lightgray",
                  borderRadius: "10px",
                  margin: "10px 0",
                }}
              >
                <Col xs={12} sm={12} md={12} style={{ margin: "15px 0" }}>
                  <DeliveryInfoComponent
                    orderIndex={currentOrderIndex}
                    title="Livraison spécifique :"
                  />
                </Col>
              </Row>
              <Row
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  border: "1px solid lightgray",
                  borderRadius: "10px",
                  margin: "10px 0",
                }}
              >
                <Col xs={12} sm={12} md={12} style={{ margin: "15px 0" }}>
                  <ServiceInfoComponent orderIndex={currentOrderIndex} />
                </Col>
              </Row>
              <Row
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  border: "1px solid lightgray",
                  borderRadius: "10px",
                  margin: "10px 0",
                }}
              >
                <Col xs={12} sm={12} md={12} style={{ margin: "15px 0" }}>
                  <h5>Commentaires</h5>
                  <CommentaryOrderInfoComponent
                    orderIndex={currentOrderIndex}
                  />
                </Col>
              </Row>
              {currentOrderIndex === null ? (
                <Row
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                    border: "1px solid lightgray",
                    borderRadius: "10px",
                    margin: "10px 0",
                  }}
                >
                  <Col xs={12} sm={12} md={12} style={{ margin: "15px 0" }}>
                    <Button variant="success" onClick={handleAddOrder}>
                      Ajouter une commande
                    </Button>
                  </Col>
                </Row>
              ) : (
                <>
                  <Row className="align-items-center" style={{ margin: "5px 0" }} noGutters>
                    <Col xs={12} sm={12} md={6}>
                      <div
                        style={{
                          display: "inline-flex",
                          border: "1px solid #dee2e6",
                          borderRadius: "8px",
                          overflow: "hidden",
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => canSwitchToClassic && setBriqueMode(false)}
                          disabled={!canSwitchToClassic}
                          title={
                            canSwitchToClassic
                              ? "Passer en mode classique"
                              : "Retirez toutes les briques pour repasser en mode classique."
                          }
                          style={{
                            padding: "6px 16px",
                            border: "none",
                            cursor: canSwitchToClassic ? "pointer" : "not-allowed",
                            fontWeight: !briqueMode ? "600" : "400",
                            backgroundColor: !briqueMode ? "#0d6efd" : "#f8f9fa",
                            color: !briqueMode ? "#fff" : "#495057",
                            fontSize: "14px",
                            opacity: canSwitchToClassic ? 1 : 0.55,
                          }}
                        >
                          Mode classique
                        </button>
                        <button
                          type="button"
                          onClick={() => canSwitchToBriques && setBriqueMode(true)}
                          disabled={!canSwitchToBriques}
                          title={
                            canSwitchToBriques
                              ? "Passer en mode briques"
                              : "Retirez tous les articles classiques pour passer en mode briques."
                          }
                          style={{
                            padding: "6px 16px",
                            border: "none",
                            borderLeft: "1px solid #dee2e6",
                            cursor: canSwitchToBriques ? "pointer" : "not-allowed",
                            fontWeight: briqueMode ? "600" : "400",
                            backgroundColor: briqueMode ? "#6f42c1" : "#f8f9fa",
                            color: briqueMode ? "#fff" : "#495057",
                            fontSize: "14px",
                            opacity: canSwitchToBriques ? 1 : 0.55,
                          }}
                        >
                          🧱 Mode briques
                        </button>
                      </div>
                    </Col>
                    <Col xs={12} sm={12} md={6} style={{ textAlign: "right" }}>
                      <Button onClick={reloadOffer} variant="warning">
                        Recharger l'offre
                      </Button>
                    </Col>
                  </Row>
                  {!briqueMode && (
                    <>
                      <Row
                        style={{
                          backgroundColor: "rgba(255, 255, 255, 0.8)",
                          border: "1px solid lightgray",
                          borderRadius: "10px",
                          margin: "10px 0",
                        }}
                      >
                        <Col xs={12} sm={12} md={12} style={{ margin: "15px 0" }}>
                          <ProductPackagesComponent
                            title="Les forfaits"
                            orderIndex={currentOrderIndex}
                            products={products}
                            tenderCatalog={tenderCatalog}
                          />
                        </Col>
                      </Row>
                      <Row
                        style={{
                          backgroundColor: "rgba(255, 255, 255, 0.8)",
                          border: "1px solid lightgray",
                          borderRadius: "10px",
                          margin: "10px 0",
                        }}
                      >
                        <Col xs={12} sm={12} md={12} style={{ margin: "15px 0" }}>
                          <ProductGroupsComponent
                            title="Les groupes de produit"
                            orderIndex={currentOrderIndex}
                            products={products}
                            tenderCatalog={tenderCatalog}
                          />
                        </Col>
                      </Row>
                      <Row
                        style={{
                          backgroundColor: "rgba(255, 255, 255, 0.8)",
                          border: "1px solid lightgray",
                          borderRadius: "10px",
                          margin: "10px 0",
                        }}
                      >
                        <Col xs={12} sm={12} md={12} style={{ margin: "15px 0" }}>
                          <ProductsComponent
                            title="Autres prestations au détail"
                            orderIndex={currentOrderIndex}
                            products={products}
                            excludedClassifications={["Matériels et services"]}
                            prestationType={prestationType}
                            tenderCatalog={tenderCatalog}
                          />
                        </Col>
                      </Row>
                      <Row
                        style={{
                          backgroundColor: "rgba(255, 255, 255, 0.8)",
                          border: "1px solid lightgray",
                          borderRadius: "10px",
                          margin: "10px 0",
                        }}
                      >
                        <Col xs={12} sm={12} md={12} style={{ margin: "15px 0" }}>
                          <ProductsComponent
                            title="Matériel & services"
                            orderIndex={currentOrderIndex}
                            products={products.filter(
                              (product) =>
                                product.classificationName ===
                                "Matériels et services"
                            )}
                            classificationName="Matériels et services"
                            prestationType={prestationType}
                            tenderCatalog={tenderCatalog}
                          />
                        </Col>
                      </Row>
                    </>
                  )}
                </>
              )}

              {prestationType === "caterer" && (
                <>
                  <Row
                    style={{
                      margin: "10px 0",
                    }}
                  >
                    <Col
                      xs={12}
                      sm={12}
                      md={12}
                      style={{
                        backgroundColor: "rgba(255, 255, 255, 0.8)",
                        border: "1px solid lightgray",
                        borderRadius: "10px",
                        padding: "15px",
                      }}
                    >
                      <PricingTable
                        orderIndex={currentOrderIndex}
                      />
                    </Col>
                  </Row>
                </>
              )}

              {/* ---------------------------------------------------------------- */}
              {/* Section Briques B2B — visible uniquement en mode briques       */}
              {/* Remplace les 4 blocs classiques (forfaits, groupes, etc.)      */}
              {/* ---------------------------------------------------------------- */}
              {briqueMode && currentOrderIndex !== null && (
                  <BriquesSection
                    // Repart de zéro à chaque changement de prestation : sinon les
                    // briques gardent en mémoire le contenu de la prestation précédente
                    // et peuvent le recopier dans celle qu'on vient d'ouvrir.
                    key={`prestation-${currentOrderIndex}`}
                    briquesDevis={briquesDevis}
                    onChange={(next) =>
                      dispatch(setOrderBriqueDevis(currentOrderIndex, next))
                    }
                    formatTagId={order?.formatTagId || null}
                    onRemoveBrique={handleRemoveBriqueFromDevis}
                    orderDndKey={String(currentOrderIndex)}
                  />
              )}

              {/* Récap de rentabilité — admins uniquement, en mode briques (lecture seule) */}
              {briqueMode && currentOrderIndex !== null && isAdmin && (
                <MarginSummaryPanel
                  briquesDevis={briquesDevis}
                  products={products}
                  productGroups={productGroups}
                  productPackages={productPackages}
                  productParts={productPartsState.data}
                  reduction={order?.reduction || {}}
                  reductions={Array.isArray(order?.reductions) ? order.reductions : null}
                />
              )}

              <Row
                className="justify-content-between"
                style={{
                  margin: "10px 0",
                }}
              >
                {!briqueMode ? (
                  <Col
                    xs={12}
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 0.8)",
                      border: "1px solid lightgray",
                      borderRadius: "10px",
                      padding: "15px",
                      maxWidth: "100%",
                    }}
                  >
                    <h5>Frais de livraison</h5>
                    <DeliveryFeeComponent orderIndex={currentOrderIndex} />
                  </Col>
                ) : briquelivraison ? (
                  <Col
                    xs={12}
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 0.8)",
                      border: "1px solid lightgray",
                      borderRadius: "10px",
                      padding: "15px",
                      maxWidth: "100%",
                    }}
                  >
                    <h5>Livraison</h5>
                    <div className="text-muted small mb-1">
                      Calculé depuis la brique <strong>{briquelivraison.name}</strong>
                    </div>
                    <div style={{ fontSize: "1.1rem", fontWeight: 600 }}>
                      {(briquelivraisonTotal / 100).toFixed(2)} € HT
                    </div>
                  </Col>
                ) : null}
                <Col
                  xs={12}
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                    border: "1px solid lightgray",
                    borderRadius: "10px",
                    padding: "15px",
                    maxWidth: "100%",
                    marginTop: "20px",
                  }}
                >
                  <h5>Réduction</h5>
                  <ReductionComponent orderIndex={currentOrderIndex} />
                </Col>
              </Row>
            </div>
          )}

          <div
            id="cart"
          >
            {currentOrderIndex !== null && (
              <Row>
                <Col xs={12} sm={12} md={12} style={{ margin: "10px 0" }}>
                  {!showAddPrestationForm ? (
                    <Button
                      variant="outline-primary"
                      onClick={() => setShowAddPrestationForm(true)}
                    >
                      Ajouter une prestation
                    </Button>
                  ) : (
                    <>
                      <small className="text-muted d-block mb-2">
                        Choisissez un format — le type de prestation (PR/PDJ/TR) est déduit automatiquement.
                      </small>
                      <Form.Group className="mb-2">
                        <Form.Control
                          as="select"
                          value={addPrestationFormatTag?.id ?? ""}
                          onChange={(e) => {
                            const id = e.target.value ? parseInt(e.target.value, 10) : null;
                            const tag = formatTags.find((t) => t.id === id) || null;
                            setAddPrestationFormatTag(tag);
                          }}
                        >
                          <option value="">-- Format --</option>
                          {formatTags.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}{t.prestation_type ? ` (${toFrenchType(t.prestation_type)})` : ""}
                            </option>
                          ))}
                        </Form.Control>
                      </Form.Group>
                      <div className="mb-2">
                        <Button
                          variant="primary"
                          size="sm"
                          className="mr-1"
                          disabled={!addPrestationFormatTag || !addPrestationFormatTag.prestation_type}
                          onClick={() => {
                            // Le `prestation_type` est porté par le format tag (cf. seed §1.5 backend).
                            // Le `SET_FORMAT` reducer le repropagera aussi sur l'order au cas où le
                            // user change de format par la suite — ici on l'utilise dès la création
                            // pour que `addOrder` initialise l'order avec le bon type.
                            handleAddOrder(addPrestationFormatTag.prestation_type, addPrestationFormatTag);
                            setShowAddPrestationForm(false);
                            setAddPrestationFormatTag(null);
                          }}
                        >
                          + Ajouter
                          {addPrestationFormatTag?.prestation_type && (
                            <span className="ml-1 text-light" style={{ opacity: 0.85 }}>
                              ({toFrenchType(addPrestationFormatTag.prestation_type)})
                            </span>
                          )}
                        </Button>
                      </div>
                      <Button variant="link" size="sm" className="p-0" onClick={() => { setShowAddPrestationForm(false); setAddPrestationFormatTag(null); }}>
                        Annuler
                      </Button>
                    </>
                  )}
                </Col>
              </Row>
            )}
            <Row>
              <Col xs={12} sm={12} md={12} style={{ margin: "10px 0" }}>
                {currentOrderIndex !== null ? (
                  <ListOrdersComponent />
                ) : (
                  <p>
                    <i>Aucune prestation</i>
                  </p>
                )}
              </Col>
            </Row>
            <hr />
            <Row>
              <Col xs={12} sm={12} md={12} style={{ margin: "10px 0" }}>
                <h5>Info complémentaires</h5>
                <DiversOrderInfoComponent />
              </Col>
            </Row>
          </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default MultiOrders;
